import React from 'react';
import { Callout, Step, DocHeading, DocTable, Shot } from './components';

function PageEsp({ navigate }) {
  return (
    <div>
      <h1 className="text-[2.5rem] leading-[1.1] font-semibold text-white tracking-[-0.02em] mb-3">ESP</h1>
      <p className="text-gray-400 leading-relaxed mb-8">
        ESP draws what is around you straight into the game world: monsters with a box and a name
        plate, loot on the ground, chests, gathering nodes, shrines and portals, the spots an enemy
        attack is about to hit, and arrows at the edge of the screen for what you cannot see.
        Everything is on <strong className="text-white">VISUALS &rsaquo; ESP</strong>.
      </p>

      <Callout type="info">
        ESP only draws. It never targets, moves or clicks anything &mdash; turning something on
        here does not make the bot act on it. It costs nothing while it is off.
      </Callout>

      <DocHeading>Getting a Useful Picture</DocHeading>
      <Step n={1} title="Switch on Show ESP">
        In the <strong>ESP</strong> card. The number in the corner of the card is how many things
        are drawn on your screen right now. A green dot shows next to <strong>ESP</strong> in the
        list on the left.
      </Step>
      <Step n={2} title="Pick a preset">
        <strong>Farming</strong> &mdash; loot, chests and gathering, monsters from elite up.{' '}
        <strong>Bosses</strong> &mdash; only the big ones: guardians, bosses and good loot.{' '}
        <strong>Everything</strong> &mdash; everything the bot can see.{' '}
        <strong>Custom</strong> &mdash; your own mix; it switches to this by itself when you change
        anything below.
      </Step>
      <Step n={3} title="Set the Range">
        How far out it draws (default 60 m). Large values on a crowded map make the screen busy.
      </Step>
      <Step n={4} title="Fine-tune in SHOW">
        Every row has its own switch. A row with an arrow at its end opens: click the arrow (or the
        row) to see its options.
      </Step>

      <Shot
        name="esp-01-esp-tab.png"
        tab="VISUALS › ESP"
        caption="The ESP tab with the Everything preset, the Ground loot row opened, and the live preview on the right."
        shoot="menushot (cfg esp.enabled=1, kattintás a Ground loot nyilára): Show ESP BE, Everything, a Ground loot sor lenyitva a ritkaság-chipekkel, jobbra a PREVIEW és a LABELS kártya."
      />

      <DocHeading>The SHOW Card</DocHeading>
      <DocTable
        headers={['Row', 'Its options when opened']}
        rows={[
          [<strong className="text-white">Monsters</strong>, 'Which ranks: Normal, Elite, Guardian, Boss.'],
          [<strong className="text-white">Mercenaries</strong>, 'Friendly fighters: mercenaries and companions. No options.'],
          [<strong className="text-white">Ground loot</strong>, 'The lowest rarity to show: Common, Uncommon, Magic, Rare, Epic, Unique. The chips from your pick upward are lit.'],
          [<strong className="text-white">Chests</strong>, 'Which kinds: Free, Lockpick, Bloodshed.'],
          [<strong className="text-white">Gathering</strong>, 'Ores and Plants.'],
          [<strong className="text-white">Interactables</strong>, 'Shrines, Portals, Summons, Other.'],
          [<strong className="text-white">Attack warnings</strong>, <><strong>Ground attacks only</strong> (the ones you can step out of) and <strong>My own skills</strong>.</>],
          [<strong className="text-white">More: dead and depleted, decoration</strong>, 'Dead monsters, used-up nodes and scenery. Off by default - mostly noise.'],
        ]}
      />
      <p className="text-sm text-gray-400 mb-4">
        The small number before a switch is how many of that kind are drawn right now.
      </p>

      <DocHeading>What You See in the Game</DocHeading>
      <DocTable
        headers={['Thing', 'How it looks']}
        rows={[
          [<strong className="text-white">Monster</strong>, <>A corner box around it. Above it a plate: the rank icon, the name in the rank colour, a level chip and the distance. Under the plate the health bar, with a thin purple line on top while it has a shield. <em>immune</em> in place of the health when the game says it cannot be hurt right now.</>],
          [<strong className="text-white">Rank colours</strong>, <><span className="text-[#9C90E8]">Normal</span>, <span className="text-yellow-300">Elite</span>, <span className="text-orange-300">Guardian</span>, <span className="text-red-400">Boss</span>.</>],
          [<strong className="text-white">Ground loot</strong>, 'A plate in the item’s rarity colour with an icon of its kind, and a small diamond on the ground. Common items and money are drawn quietly.'],
          [<strong className="text-white">Chest, node, shrine, portal</strong>, 'A square badge with the icon of its kind, the name, the key it needs (if any) and the distance.'],
          [<strong className="text-white">Attack warning</strong>, 'A red circle on the spot. The area inside fills up until the moment of impact, with a countdown. Circles wider than 20 m are drawn as an outline only, so they do not cover the screen. Only monster attacks show (plus your own with My own skills).'],
        ]}
      />
      <p className="text-sm text-gray-400 mb-4">
        When labels would sit on top of each other, the more important one stays and the other
        moves up. Things further away fade (see Labels below).
      </p>

      <DocHeading>Off-Screen Arrows</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        With the switch in the <strong className="text-white">OFF-SCREEN ARROWS</strong> card on, an
        arrow at the edge of the screen points to what you cannot see. Pick which monster ranks get
        an arrow, and the other kinds: Chests, Loot from Epic, Shrines, Portals, Gathering,
        Mercenaries. <strong className="text-white">At most</strong> sets how many arrows, nearest
        first.
      </p>

      <DocHeading>Preview and Labels</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        The <strong className="text-white">PREVIEW</strong> card draws a small sample scene with your
        current settings &mdash; <em>changes show here at once</em>, so you can try things without
        walking into a map.
      </p>
      <DocTable
        headers={['Setting', 'What it does']}
        rows={[
          [<strong className="text-white">Name, Level, Distance, Health bar, Health %</strong>, 'What the monster plate shows. Click a chip to switch it.'],
          [<strong className="text-white">Text size</strong>, 'Makes every label bigger or smaller.'],
          [<strong className="text-white">Fade far away</strong>, 'How much far things fade.'],
          [<strong className="text-white">Tracers</strong>, 'Lines to each thing, from the bottom, the centre or the top of the screen. Off by default.'],
        ]}
      />

      <DocHeading>If Something Looks Off</DocHeading>
      <DocTable
        headers={['What you see', 'What to do']}
        rows={[
          ['Nothing is drawn', 'Check Show ESP, the Range, and the row switches. The count in the ESP card says how many things are drawn.'],
          ['Labels drift away from the monsters after a camera change', <>Turn on <strong>Match ESP projection</strong> on <button onClick={() => navigate('camera')} className="text-brand-300 hover:text-brand-200 underline underline-offset-2">VISUALS &rsaquo; Camera</button>.</>],
          ['Too much on the screen', 'Pick the Farming or Bosses preset, lower the Range, or raise the lowest loot rarity.'],
          ['Attack circles you do not care about', 'Keep Ground attacks only on, and My own skills off.'],
        ]}
      />
    </div>
  );
}

export default PageEsp;
